import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';
import {
  useIncomingEmails,
  useLinkIncomingEmail,
  useIgnoreIncomingEmail,
  useProcessInbox,
} from '../../api/email';
import {
  IncomingEmailStatus,
  IncomingEmailStatusConfig,
  QueryIncomingEmailDto,
  IncomingEmail,
} from '../../types/email';
import { useBewerberList } from '../../api/bewerber';

export function IncomingEmailsPage() {
  const confirm = useConfirm();
  const [query, setQuery] = useState<QueryIncomingEmailDto>({
    page: 1,
    limit: 20,
    sortBy: 'receivedAt',
    sortOrder: 'desc',
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [bewerberSearch, setBewerberSearch] = useState('');

  const { data, isLoading, error } = useIncomingEmails(query);
  const linkMutation = useLinkIncomingEmail();
  const ignoreMutation = useIgnoreIncomingEmail();
  const processInboxMutation = useProcessInbox();

  const { data: bewerberData } = useBewerberList({
    search: bewerberSearch,
    limit: 10,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery((prev) => ({ ...prev, search: searchInput || undefined, page: 1 }));
  };

  const handleStatusFilter = (status: IncomingEmailStatus | '') => {
    setQuery((prev) => ({ ...prev, status: status || undefined, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setQuery((prev) => ({ ...prev, page: newPage }));
  };

  const handleProcessInbox = async () => {
    try {
      const result = await processInboxMutation.mutateAsync();
      toast.success(`${result.data.processed} E-Mails verarbeitet`);
    } catch (error) {
      console.error('Failed to process inbox:', error);
      toast.error('Fehler beim Verarbeiten des Posteingangs');
    }
  };

  const handleIgnore = async (email: IncomingEmail) => {
    const confirmed = await confirm({
      title: 'E-Mail ignorieren',
      message: 'Möchten Sie diese E-Mail wirklich ignorieren?',
      confirmLabel: 'Ignorieren',
      variant: 'warning',
    });
    if (confirmed) {
      try {
        await ignoreMutation.mutateAsync(email.id);
        toast.success('E-Mail ignoriert');
      } catch (error) {
        console.error('Failed to ignore email:', error);
        toast.error('Fehler beim Ignorieren der E-Mail');
      }
    }
  };

  const handleLink = async (bewerberId: string) => {
    if (!selectedEmail) return;
    try {
      await linkMutation.mutateAsync({
        id: selectedEmail,
        data: { bewerberId },
      });
      toast.success('E-Mail erfolgreich verknuepft');
      setLinkModalOpen(false);
      setSelectedEmail(null);
    } catch (error) {
      console.error('Failed to link email:', error);
      toast.error('Fehler beim Verknuepfen der E-Mail');
    }
  };

  const selectedEmailData = selectedEmail
    ? data?.data.find((email) => email.id === selectedEmail)
    : null;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Fehler beim Laden der eingehenden E-Mails</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Eingehende E-Mails</h1>
          <p className="mt-1 text-sm text-gray-500">
            Verarbeiten und verknuepfen Sie eingehende E-Mails mit Bewerbern
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleProcessInbox}
            disabled={processInboxMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {processInboxMutation.isPending ? (
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : (
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Posteingang pruefen
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Search */}
            <form onSubmit={handleSearch} className="sm:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Suche
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Absender, Betreff..."
                  className="flex-1 block w-full rounded-l-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-700 hover:bg-gray-100"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </form>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                value={query.status || ''}
                onChange={(e) => handleStatusFilter(e.target.value as IncomingEmailStatus | '')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Alle Status</option>
                {Object.entries(IncomingEmailStatusConfig).map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Table */}
        <div className={`bg-white shadow rounded-lg overflow-hidden ${selectedEmail ? 'flex-1' : 'w-full'}`}>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Laden...</p>
            </div>
          ) : data?.data.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Keine eingehenden E-Mails</h3>
              <p className="mt-1 text-sm text-gray-500">
                Klicken Sie auf "Posteingang pruefen" um neue E-Mails abzurufen.
              </p>
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Absender
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Betreff
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empfangen
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Aktionen</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.data.map((email) => {
                    const statusConfig = IncomingEmailStatusConfig[email.status];
                    return (
                      <tr
                        key={email.id}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          selectedEmail === email.id ? 'bg-primary-50' : ''
                        }`}
                        onClick={() => setSelectedEmail(selectedEmail === email.id ? null : email.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {email.fromName || email.fromAddress}
                          </div>
                          <div className="text-xs text-gray-500">{email.fromAddress}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 truncate max-w-xs">
                            {email.subject}
                          </div>
                          {email.bewerber && (
                            <div className="text-xs text-gray-500">
                              <Link
                                to={`/bewerber/${email.bewerber.id}`}
                                className="text-primary-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {email.bewerber.firstName} {email.bewerber.lastName}
                              </Link>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}
                          >
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(email.receivedAt).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                            {email.status === IncomingEmailStatus.UNPROCESSED && (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedEmail(email.id);
                                    setLinkModalOpen(true);
                                  }}
                                  className="text-primary-600 hover:text-primary-900"
                                >
                                  Verknuepfen
                                </button>
                                <button
                                  onClick={() => handleIgnore(email)}
                                  className="text-gray-600 hover:text-gray-900"
                                  disabled={ignoreMutation.isPending}
                                >
                                  Ignorieren
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {data?.meta && data.meta.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-700">
                      Zeige{' '}
                      <span className="font-medium">{(data.meta.page - 1) * data.meta.limit + 1}</span>
                      {' '}-{' '}
                      <span className="font-medium">
                        {Math.min(data.meta.page * data.meta.limit, data.meta.total)}
                      </span>
                      {' '}von{' '}
                      <span className="font-medium">{data.meta.total}</span>
                    </p>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(data.meta!.page - 1)}
                        disabled={data.meta!.page <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        Seite {data.meta.page} von {data.meta.totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(data.meta!.page + 1)}
                        disabled={data.meta!.page >= data.meta!.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail Panel */}
        {selectedEmailData && !linkModalOpen && (
          <div className="w-96 bg-white shadow rounded-lg overflow-hidden flex-shrink-0">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">E-Mail Details</h3>
              <button
                onClick={() => setSelectedEmail(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">Von</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedEmailData.fromName && (
                    <span className="font-medium">{selectedEmailData.fromName}</span>
                  )}
                  <span className="block text-gray-600">{selectedEmailData.fromAddress}</span>
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">Betreff</label>
                <p className="mt-1 text-sm text-gray-900">{selectedEmailData.subject}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">Empfangen</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedEmailData.receivedAt).toLocaleString('de-DE')}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">Status</label>
                <span
                  className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    IncomingEmailStatusConfig[selectedEmailData.status].bgColor
                  } ${IncomingEmailStatusConfig[selectedEmailData.status].color}`}
                >
                  {IncomingEmailStatusConfig[selectedEmailData.status].label}
                </span>
              </div>
              {selectedEmailData.bewerber && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Verknuepft mit</label>
                  <Link
                    to={`/bewerber/${selectedEmailData.bewerber.id}`}
                    className="mt-1 text-sm text-primary-600 hover:underline"
                  >
                    {selectedEmailData.bewerber.firstName} {selectedEmailData.bewerber.lastName}
                  </Link>
                </div>
              )}
              <div className="pt-4 border-t">
                <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Inhalt</label>
                {selectedEmailData.bodyHtml ? (
                  <div
                    className="prose prose-sm max-w-none bg-gray-50 rounded-md p-4 max-h-64 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: selectedEmailData.bodyHtml }}
                  />
                ) : (
                  <pre className="text-sm text-gray-700 bg-gray-50 rounded-md p-4 max-h-64 overflow-y-auto whitespace-pre-wrap">
                    {selectedEmailData.bodyText || 'Kein Inhalt'}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Link Modal */}
      {linkModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
              onClick={() => setLinkModalOpen(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">E-Mail verknuepfen</h3>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bewerber suchen
                  </label>
                  <input
                    type="text"
                    value={bewerberSearch}
                    onChange={(e) => setBewerberSearch(e.target.value)}
                    placeholder="Name oder E-Mail..."
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {bewerberData?.data.map((bewerber) => (
                    <button
                      key={bewerber.id}
                      onClick={() => handleLink(bewerber.id)}
                      disabled={linkMutation.isPending}
                      className="w-full text-left px-4 py-3 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <div className="font-medium text-gray-900">
                        {bewerber.firstName} {bewerber.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{bewerber.email}</div>
                    </button>
                  ))}
                  {bewerberData?.data.length === 0 && bewerberSearch && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Keine Bewerber gefunden
                    </p>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
                <button
                  onClick={() => setLinkModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
